// Type-only import: erased at build time, so this module has NO runtime dependency on
// React, the fetcher or the design system. Like `_status.ts`, it is pure data -> words,
// and the node-only test tree exercises the real functions directly.
import type { RenderedDocumentPage } from "../api/types.js";

/**
 * #1969 — every word the staff document page shows that is derived from an engine value.
 *
 * L-97 rule (d): a human never reads a raw identifier, path or flag. The engine reports
 * `template_id: "kit-document"`, `pages[].near_empty: true` and `byte_size: 187342`; the page
 * shows "Kit document, version 1", "Page 3 is almost empty…" and "183 KB". Keeping the
 * translation here, not inline in JSX, is what lets it be tested without a DOM.
 */

/** "1 page" / "4 pages". */
export function pageCountWords(count: number): string {
  return count === 1 ? "1 page" : `${count} pages`;
}

/** "3", "2 and 3", "2, 3 and 5". */
function listWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The per-page note for pages the engine's raster check found almost blank, or `null` when
 * every page carries content. A near-empty page is a warning to a person, not a refusal —
 * the engine still stored the document — so the note asks them to look before they send it.
 */
export function nearEmptyNote(pages: readonly RenderedDocumentPage[]): string | null {
  const flagged = pages
    .filter((page) => page.near_empty)
    .map((page) => page.n)
    .sort((a, b) => a - b)
    .map(String);
  if (flagged.length === 0) return null;
  if (flagged.length === 1) {
    return `Page ${flagged[0]} is almost empty. Check it before you send the document.`;
  }
  return `Pages ${listWords(flagged)} are almost empty. Check them before you send the document.`;
}

/**
 * The template in words: `kit-document` @ 1 -> "Kit document, version 1". The id is the
 * engine's template name, humanised (separators to spaces, first letter capitalised) rather
 * than looked up in a label table the console would have to keep in step with the engine.
 */
export function templateWords(templateId: string, templateVersion: string | number): string {
  const words = templateId
    .split(/[-_.\s]+/)
    .filter((part) => part.length > 0)
    .join(" ")
    .toLowerCase();
  const name = words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : "Document template";
  const version = String(templateVersion).trim().replace(/^v(?=\d)/i, "");
  return version.length > 0 ? `${name}, version ${version}` : name;
}

/** "812 bytes" / "183 KB" / "1.4 MB" (binary multiples, as a file manager shows them). */
export function byteSizeWords(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "Not recorded";
  if (bytes < 1024) return bytes === 1 ? "1 byte" : `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * When the document was made, in the reader's own locale and time zone
 * ("25 September 2026 at 15:18"). `locale` / `timeZone` exist for the tests only.
 */
export function createdWords(iso: string, locale?: string, timeZone?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "Not recorded";
  return at.toLocaleString(locale, {
    dateStyle: "long",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  });
}

/** Why the page could not show a document. */
export type DocumentLoadFailure = "not_found" | "forbidden" | "session_expired" | "unavailable";

/**
 * Classify a failed read. `status` is the engine's HTTP status (undefined for a network
 * failure); `sessionExpired` is true when the fetcher gave up on the sign-in.
 *
 * 404 is the engine's MASKED answer: an unknown id and another tenant's id look the same, so
 * the page must not say which. A 400 (a malformed id in a hand-edited link) reads the same way.
 */
export function classifyDocumentFailure(
  status: number | undefined,
  sessionExpired: boolean,
): DocumentLoadFailure {
  if (sessionExpired || status === 401) return "session_expired";
  if (status === 404 || status === 400) return "not_found";
  if (status === 403) return "forbidden";
  return "unavailable";
}

/** The sentence the page shows for each failure. */
export const DOCUMENT_FAILURE_WORDS: Readonly<Record<DocumentLoadFailure, string>> = {
  not_found: "Document not found. Check the link you were given.",
  forbidden: "Your account does not have permission to view documents.",
  session_expired: "Your session has expired. Reload the page to sign in again.",
  unavailable: "Couldn't reach the server. Try again.",
};

/** The sentence under the viewer when the metadata loaded but the PDF itself did not. */
export const PDF_UNAVAILABLE_WORDS = "The PDF could not be loaded. Reload the page to try again.";
